import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { propertyService } from 'services/api';

export const STEP_INFO  = 1;
export const STEP_SETUP = 2;
const defaultProperty = { name: '', address: '', city: '', phone: '', nit: '', tipo_propiedad: 'residencial' };
// Nombre/dirección/ciudad/NIT obligatorios — trim antes de exigir "no vacío"
// porque un valor de solo espacios pasaba el `required` nativo del input.
// La longitud ya la bloquea el `maxLength` del input en un uso normal, pero
// se revisa también aquí explícitamente (igual que valida el backend) para
// no depender solo del atributo HTML ahora que los formularios llevan
// `noValidate`.
function validarCamposObligatorios(form) {
  if (!form.name.trim())    return 'El nombre de la propiedad es requerido';
  if (!form.address.trim()) return 'La dirección es requerida';
  if (!form.city.trim())    return 'La ciudad es requerida';
  if (!form.nit.trim())     return 'El NIT es requerido';
  if (!form.phone.trim())   return 'El teléfono es requerido';
  for (const campo of ['name', 'address', 'city']) {
    if (form[campo].length > FIELD_MAX_LENGTH[campo]) {
      return `El campo "${campo}" no puede tener más de ${FIELD_MAX_LENGTH[campo]} caracteres`;
    }
  }
  if (!NIT_REGEX.test(form.nit.trim())) return 'El NIT no es válido — debe ser numérico (ej. 900123456 o 900123456-7)';
  if (!PHONE_REGEX.test(form.phone.trim())) {
    return 'El teléfono no es válido — debe tener 7 (fijo) o 10 (celular) dígitos, sin espacios ni +57';
  }
  return null;
}
// Mismos límites que ya tienen los inputs numéricos (min/max) más abajo —
// el atributo HTML solo bloquea el envío en un navegador normal; esta
// validación explícita da un mensaje claro en vez de depender solo del
// tooltip nativo, igual que valida el backend (utils/validators no aplica
// aquí porque son límites propios de Propiedades, no un formato reutilizable).
const LIMITES_BLOQUE = { towers: 200, floors: 200, units_per_floor: 600 };
// Límite del TOTAL (towers × floors × units_per_floor) — mismo criterio que
// valida el backend: un valor dentro de rango en cada campo por separado
// (ej. 200 torres × 200 pisos × 600 unidades/piso = 24 millones) igual podía
// intentar crear una cantidad de filas que tumbaría la base de datos.
// Diferenciado del límite por propiedad (más abajo): no existe un bloque
// real de 6,000 unidades, pero se deja ese margen a propósito; una propiedad
// sí puede legítimamente sumar más que un bloque cualquiera si combina
// varios tipos (torres + casas + locales).
const MAX_UNIDADES_BLOQUE = 6000;
const MAX_UNIDADES_PROPIEDAD = 12000;
function validarBloques(bloques) {
  for (const b of bloques) {
    if (!b.label.trim()) return 'Cada bloque necesita una etiqueta (ej. "Torre", "Casa", "Local")';
    for (const [campo, max] of Object.entries(LIMITES_BLOQUE)) {
      const val = Number(b[campo]);
      if (!Number.isInteger(val) || val < 1 || val > max) {
        return `El bloque "${b.label}" tiene un valor inválido — debe ser un número entero entre 1 y ${max}`;
      }
    }
    const total = b.towers * b.floors * b.units_per_floor;
    if (total > MAX_UNIDADES_BLOQUE) {
      return `El bloque "${b.label}" tendría ${total} unidades en total — el máximo permitido por bloque es ${MAX_UNIDADES_BLOQUE}`;
    }
  }
  // Además del tope por bloque, aplica a la SUMA de todos los bloques —
  // varios bloques individualmente razonables podían sumar una cantidad
  // igual de descontrolada de unidades.
  const totalPropiedad = bloques.reduce((sum, b) => sum + b.towers * b.floors * b.units_per_floor, 0);
  if (totalPropiedad > MAX_UNIDADES_PROPIEDAD) {
    return `Esta propiedad tendría ${totalPropiedad} unidades en total (sumando todos los bloques) — el máximo permitido es ${MAX_UNIDADES_PROPIEDAD}`;
  }
  return null;
}
// Cada tipo de bloque trae su propia etiqueta y tipo_inmueble por defecto.
// Cualquier tipo puede tener varios edificios (un centro comercial rara vez
// tiene 2-3, un conjunto de casas puede tener varias manzanas). Solo "Casas"
// es singleUnit: cada casa ES el edificio, no hay pisos ni unidades dentro de
// otra unidad — "Número de casas" ya define directamente cuántas se crean.
// "label" es singular a propósito en TODOS los tipos — es lo que se usa para
// nombrar cada edificio individual (ej. "Torre 1", "Casa 1"). towersLabel y
// unitLabel sí quedan en plural porque son preguntas sobre la categoría
// completa ("¿Número de casas?"), no el nombre de un edificio puntual.
// Exportado: la ayuda del importador (Owners.jsx) lista estos mismos labels,
// que son los únicos tipo_bloque que acepta el backend (CATEGORIAS_BLOQUE en owners.js).
export const BLOQUE_TIPOS = [
  { key: 'torre',        label: 'Torre',        towersLabel: 'Número de torres',        unitLabel: 'Unidades',     tipo_inmueble: 'apartamento', hasFloors: true,  singleUnit: false },
  { key: 'casas',        label: 'Casa',         towersLabel: 'Número de casas',         unitLabel: 'Casas',        tipo_inmueble: 'casa',        hasFloors: false, singleUnit: true  },
  { key: 'locales',      label: 'Local',        towersLabel: 'Edificios de locales',    unitLabel: 'Locales',      tipo_inmueble: 'local',       hasFloors: true,  singleUnit: false },
  { key: 'oficinas',     label: 'Oficina',      towersLabel: 'Edificios de oficinas',   unitLabel: 'Oficinas',     tipo_inmueble: 'oficina',     hasFloors: true,  singleUnit: false },
  { key: 'parqueaderos', label: 'Parqueadero',  towersLabel: 'Número de parqueaderos',  unitLabel: 'Parqueaderos', tipo_inmueble: 'parqueadero', hasFloors: true,  singleUnit: false },
  { key: 'bodegas',      label: 'Bodega',       towersLabel: 'Número de bodegas',       unitLabel: 'Bodegas',      tipo_inmueble: 'bodega',      hasFloors: true,  singleUnit: false },
];
export const bloqueTipoInfo = key => BLOQUE_TIPOS.find(t => t.key === key) || BLOQUE_TIPOS[0];
const newBloque = () => {
  const t = BLOQUE_TIPOS[0];
  return { tipo: t.key, label: t.label, towers: 1, floors: 1, units_per_floor: 1, tipo_inmueble: t.tipo_inmueble };
};
const defaultBloques = () => [newBloque()];
// Límites razonables por campo (la columna en BD es varchar libre de 255,
// esto solo evita que alguien pegue un párrafo entero en "Nombre").
export const FIELD_MAX_LENGTH = { name: 150, address: 200, city: 100, phone: 10, nit: 17 };
// NIT colombiano: solo dígitos, con dígito de verificación opcional tras un
// guion (ej. "900123456-7") — mismo formato que valida el backend.
const NIT_REGEX = /^\d{5,15}(-\d)?$/;
// Teléfono colombiano: solo dígitos, fijo (7) o celular (10), sin +57 ni
// separadores — mismo formato que valida el backend (utils/validators.js).
const PHONE_REGEX = /^\d{7,10}$/;

// Lógica de la pantalla (estado, carga y acciones); la página solo pinta.
export default function usePropertyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [step,     setStep]     = useState(STEP_INFO);
  const [form,     setForm]     = useState(defaultProperty);
  const [bloques,  setBloques]  = useState(defaultBloques);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    setLoadingData(true);
    propertyService.getById(id)
      .then(r => {
        const d = r.data || {};
        setForm({
          name:           d.name           || '',
          address:        d.address        || '',
          city:           d.city           || '',
          phone:          d.phone          || '',
          nit:            d.nit            || '',
          tipo_propiedad: d.tipo_propiedad || 'residencial',
        });
      })
      .catch(err => setError('Error al cargar la propiedad: ' + (err.response?.data?.message || err.message)))
      .finally(() => setLoadingData(false));
  }, [id, isEdit]);

  const chForm  = e => setForm(f  => ({ ...f,  [e.target.name]: e.target.value }));

  const NUMERIC_FIELDS = ['towers', 'floors', 'units_per_floor'];
  const chBloque = (idx, field, value) => setBloques(list => list.map((b, i) => (
    i !== idx ? b : { ...b, [field]: NUMERIC_FIELDS.includes(field) ? Math.max(1, parseInt(value) || 1) : value }
  )));
  // Cambiar el tipo de bloque reinicia sus cantidades — evita arrastrar, por
  // ejemplo, un número de "pisos" que ya no aplica al pasar de Torre a Casas.
  const chBloqueTipo = (idx, tipoKey) => {
    const t = bloqueTipoInfo(tipoKey);
    setBloques(list => list.map((b, i) => (
      i !== idx ? b : { ...b, tipo: t.key, label: t.label, tipo_inmueble: t.tipo_inmueble, towers: 1, floors: 1, units_per_floor: 1 }
    )));
  };
  const addBloque    = () => setBloques(list => [...list, newBloque()]);
  const removeBloque = idx => setBloques(list => list.length > 1 ? list.filter((_, i) => i !== idx) : list);

  // EDITAR: flujo simple de siempre
  const submitEdit = async (e) => {
    e.preventDefault();
    const campoError = validarCamposObligatorios(form);
    if (campoError) return setError(campoError);
    setLoading(true);
    // Validar NIT único antes de guardar
    try {
      const { data } = await propertyService.checkNit(form.nit.trim(), id);
      if (data.exists) {
        setError('El NIT ingresado ya se encuentra registrado para otra propiedad.');
        setLoading(false);
        return;
      }
    } catch (_) {}
    try { await propertyService.update(id, form); navigate('/admin/propiedades'); }
    catch (err) { setError(err.response?.data?.message || 'Error'); setLoading(false); }
  };

  // CREAR paso 1 → paso 2
  const nextStep = async (e) => {
    e.preventDefault();
    const campoError = validarCamposObligatorios(form);
    if (campoError) return setError(campoError);
    // Validar NIT único antes de avanzar al paso 2
    try {
      const { data } = await propertyService.checkNit(form.nit.trim(), id);
      if (data.exists) {
        return setError('El NIT ingresado ya se encuentra registrado para otra propiedad.');
      }
    } catch (_) {}
    setError('');
    setStep(STEP_SETUP);
  };

  // CREAR paso 2 → enviar
  const submitCreate = async (e) => {
    e.preventDefault();
    const bloqueError = validarBloques(bloques);
    if (bloqueError) return setError(bloqueError);
    setLoading(true); setError('');
    try {
      await propertyService.setup({ property: form, bloques });
      navigate('/admin/propiedades');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear la propiedad');
      setLoading(false);
    }
  };

  const totalUnitsAll = bloques.reduce((sum, b) => sum + b.towers * b.floors * b.units_per_floor, 0);

  return {
    step, setStep, form, bloques, loading, error, loadingData, isEdit, chForm, chBloque, chBloqueTipo,
    addBloque, removeBloque, submitEdit, nextStep, submitCreate, totalUnitsAll,
  };
}
